import React, { useState, useEffect, useMemo } from 'react';
import { Search, UserSquare } from 'lucide-react';
import api from '../services/api';

// Seletor de contato(s) pra enviar como vCard dentro de uma conversa.
// Duas fontes: a agenda do tenant (aba "Agenda") e digitação manual (aba
// "Digitar"), pra mandar um número que ainda não está cadastrado.
const MAX_CONTACTS = 5;

function ContactPickerModal({ onConfirm, onCancel }) {
  const [tab, setTab] = useState('agenda');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [manual, setManual] = useState({ name: '', number: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/contacts');
        if (alive) setContacts(res.data || []);
      } catch {
        if (alive) setContacts([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? contacts.filter(c =>
          (c.name || '').toLowerCase().includes(q) ||
          (c.phone_number || '').includes(q.replace(/\D/g, ''))
        )
      : contacts;
    return list.slice(0, 80);
  }, [contacts, search]);

  const isSelected = (number) => selected.some(s => s.number === number);

  const toggle = (c) => {
    const number = String(c.phone_number || '').replace(/\D/g, '');
    if (!number) return;
    setSelected(prev => {
      if (prev.some(s => s.number === number)) return prev.filter(s => s.number !== number);
      if (prev.length >= MAX_CONTACTS) return prev;
      return [...prev, { name: c.name || c.phone_number, number }];
    });
  };

  const addManual = () => {
    const number = manual.number.replace(/\D/g, '');
    if (number.length < 10) return;
    if (selected.length >= MAX_CONTACTS) return;
    if (isSelected(number)) return;
    setSelected(prev => [...prev, { name: manual.name.trim() || number, number }]);
    setManual({ name: '', number: '' });
  };

  const confirm = async () => {
    if (!selected.length || sending) return;
    setSending(true);
    try {
      await onConfirm(selected);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal contact-picker" onClick={e => e.stopPropagation()}>
        <h3>Enviar contato</h3>

        <div className="contact-picker-tabs">
          <button
            className={`contact-picker-tab ${tab === 'agenda' ? 'active' : ''}`}
            onClick={() => setTab('agenda')}
          >
            Agenda
          </button>
          <button
            className={`contact-picker-tab ${tab === 'manual' ? 'active' : ''}`}
            onClick={() => setTab('manual')}
          >
            Digitar número
          </button>
        </div>

        <div className="modal-body">
          {tab === 'agenda' ? (
            <>
              <div className="contact-picker-search">
                <Search size={15} />
                <input
                  autoFocus
                  placeholder="Buscar por nome ou número"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="contact-picker-list">
                {loading && <p className="contact-picker-empty">Carregando…</p>}
                {!loading && filtered.length === 0 && (
                  <p className="contact-picker-empty">Nenhum contato encontrado.</p>
                )}
                {filtered.map(c => {
                  const number = String(c.phone_number || '').replace(/\D/g, '');
                  return (
                    <label key={c.id} className="contact-picker-row">
                      <input
                        type="checkbox"
                        checked={isSelected(number)}
                        onChange={() => toggle(c)}
                      />
                      <UserSquare size={16} className="contact-picker-icon" />
                      <span className="contact-picker-name">{c.name || c.phone_number}</span>
                      <span className="contact-picker-number">{c.phone_number}</span>
                    </label>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="contact-picker-manual">
              <div className="modal-field">
                <label>Nome</label>
                <input
                  autoFocus
                  placeholder="Ex: João da Silva"
                  value={manual.name}
                  onChange={e => setManual(m => ({ ...m, name: e.target.value }))}
                />
              </div>
              <div className="modal-field">
                <label>Número (com DDI e DDD)</label>
                <input
                  placeholder="Ex: 5511988887777"
                  value={manual.number}
                  onChange={e => setManual(m => ({ ...m, number: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } }}
                />
              </div>
              <button
                className="btn-secondary"
                onClick={addManual}
                disabled={manual.number.replace(/\D/g, '').length < 10 || selected.length >= MAX_CONTACTS}
              >
                Adicionar à lista
              </button>
            </div>
          )}

          {selected.length > 0 && (
            <div className="contact-picker-selected">
              {selected.map(s => (
                <span key={s.number} className="contact-picker-chip">
                  {s.name}
                  <button onClick={() => setSelected(prev => prev.filter(p => p.number !== s.number))}>×</button>
                </span>
              ))}
            </div>
          )}
          {selected.length >= MAX_CONTACTS && (
            <small className="contact-picker-help">
              Máximo de {MAX_CONTACTS} contatos por envio.
            </small>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={sending}>Cancelar</button>
          <button className="btn-primary" onClick={confirm} disabled={!selected.length || sending}>
            {sending ? 'Enviando…' : `Enviar${selected.length > 1 ? ` (${selected.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ContactPickerModal;
